import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useRef, useState } from 'react';

import {
  getFeatureFlag,
  setDatadogGlobalContext,
  setUserProperties,
  trackEvent,
} from '@/modules/analytics';
import {
  fetchCoachAvailability,
  fetchHistoricalMetricPoints,
  fetchMatchedCoaches,
  fetchPartners,
  fetchPrescribedPrograms,
  fetchTeamsV5,
  fetchUserProgramInstances,
  hasPrimaryCoach,
} from '@/modules/api';
import type { CoachAvailability, MatchedCoaches, PrescribedPrograms } from '@/modules/api';
import type { User } from '@/types/user';

import { FEATURE_FLAGS } from '../constants/featureFlags';
import type { PartnersState } from '../types/partnersState';
import type { ResourceState } from '../types/resolution';
import { UNSET } from '../types/resolution';
import { isSurveyComplete } from '../utils/isSurveyComplete';
import { programRecommendationType } from '../utils/programRecommendationType';

type UseOnboardingResourcesArgs = {
  /** `partners` state stays in the core provider (eligibility + the driver read it). */
  setPartners: Dispatch<SetStateAction<PartnersState>>;
  /** Read by `ensureMetricPoints` for the org-aware survey-complete gate. */
  userDataRef: RefObject<User | undefined>;
  /**
   * Owned by the provider (read by sendIntroMessage). `ensureTeams` captures the
   * solo care-team's uuid into it; `resetResources` clears it. Passed in (like
   * userDataRef) so the provider's consumer keeps an eslint-stable ref.
   */
  soloTeamUuidRef: RefObject<string | undefined>;
};

export type UseOnboardingResources = {
  // Driver-fetched resource state (the resolver/driver + value-object mock
  // fallbacks read these). `value` is the derived boolean once `ready`:
  //   programInstances → hasProgram   metricPoints → surveyComplete
  //   teams            → hasCoach
  programInstances: ResourceState<boolean>;
  metricPoints: ResourceState<boolean>;
  teams: ResourceState<boolean>;
  prescribedPrograms: ResourceState<PrescribedPrograms>;
  matchedCoaches: ResourceState<MatchedCoaches>;
  coachAvailability: Record<string, ResourceState<CoachAvailability>>;
  /**
   * `onb_use_dynamic_assessment` experiment arm, read client-side from PostHog.
   * `value === true` is the `test` arm (serve the onboarding surveys via the
   * dynamic-assessment endpoint instead of the legacy screens). Consumed by the
   * resolver (the survey start is arm-dependent: /step/dynamic-assessment vs
   * /step/health-conditions), by DynamicAssessmentScreen for the arm + readiness,
   * and by AssignedScreenersScreen to pick its `excludeKeys`. `error` fails open
   * to control (non-true reads as legacy).
   */
  dynamicAssessmentEnabled: ResourceState<boolean>;
  /**
   * Latched by DynamicAssessmentScreen when a dynamic-assessment pass fails
   * (load/submit error). All-or-nothing: the resolver reads it to abandon the
   * whole survey region and skip to the program region for the rest of the
   * session — no legacy fallback. Cleared on per-user reset.
   */
  dynamicAssessmentErrored: boolean;
  markDynamicAssessmentErrored: () => void;
  /**
   * The dynamic-assessment flow keys the pass drove to `completed` (from the
   * final `/flows/` snapshot), latched by DynamicAssessmentScreen on a clean
   * finish. `dynamicSurveyNextStep` reads it (via a ref in `goToNextStep`) to
   * decide whether the standalone pre-diabetes / phq-gad steps were already
   * covered as dynamic flows — a per-survey check, NOT "were we in the test
   * arm." So a flow that was never published or landed `abandoned` (never
   * `completed`) still routes to its standalone screener instead of being
   * silently dropped. Cleared on per-user reset.
   */
  dynamicCompletedFlowKeys: readonly string[];
  setDynamicCompletedFlowKeys: (keys: readonly string[]) => void;

  loadPartners: (isTester?: boolean) => Promise<void>;
  ensureProgramInstances: (userUri: string) => void;
  ensureMetricPoints: () => void;
  ensureTeams: (soloTeamCode: string | undefined) => void;
  ensurePrescribedPrograms: (userUrn: string) => void;
  ensureMatchedCoaches: (customerUri: string) => void;
  ensureCoachAvailability: (providerUuid: string) => void;
  /** Read the `onb_use_dynamic_assessment` flag once (deduped) into `dynamicAssessmentEnabled`. */
  ensureDynamicAssessment: () => void;

  /**
   * Re-arm `teams` so the driver re-pulls it (group auto-assign assigns a coach).
   * Owns the teams dedup ref, so the program hook's `enrollInProgram` goes through
   * here rather than reaching for the ref directly.
   */
  rearmTeams: () => void;
  /**
   * Mark the onboarding survey answered for this session (metricPoints → ready:true)
   * so the driver routes past the survey. Shared by phq-gad completion, crisis
   * entry, and goToNextStep's dynamic-survey exhaustion — an intent-method instead
   * of leaking `setMetricPoints` across hooks.
   */
  markSurveyComplete: () => void;
  /** Mark a program instance created (programInstances → ready:true) so the driver advances to coach matching. */
  markProgramEnrolled: () => void;
  /**
   * Mark provider matching complete (the `teams` = hasCoach resource → ready:true)
   * without a refetch. Called at the coach-phase exit after a manual coach
   * assignment (`assignPrimaryCoach` deliberately does not flip it — see IT-C2), so
   * a later driver re-resolve (e.g. member-contact's mount refresh) doesn't bounce
   * the member back to coach matching.
   */
  markProviderMatchingComplete: () => void;
  /**
   * Resets every resource state + per-user dedup ref + the captured solo-team
   * uuid. Called by the provider's per-user reset. `partnersFetchStarted` /
   * `partners` are intentionally left alone (partners are cached app-lifetime).
   */
  resetResources: () => void;
};

/**
 * The rules-engine's network layer: the driver-fetched resource STATE + the
 * loaders that fill it + the per-resource fetch-dedup refs. State lives here (not
 * in the core provider) and is returned for the resolver/driver/value to read;
 * each loader fetches once (dedup ref), writes loading → ready/error, and fails
 * open (error → the safe default the resolver reads as `false`).
 */
export function useOnboardingResources({
  setPartners,
  userDataRef,
  soloTeamUuidRef,
}: UseOnboardingResourcesArgs): UseOnboardingResources {
  const [programInstances, setProgramInstances] = useState<ResourceState<boolean>>(UNSET);
  const [metricPoints, setMetricPoints] = useState<ResourceState<boolean>>(UNSET);
  const [teams, setTeams] = useState<ResourceState<boolean>>(UNSET);
  const [prescribedPrograms, setPrescribedPrograms] =
    useState<ResourceState<PrescribedPrograms>>(UNSET);
  const [matchedCoaches, setMatchedCoaches] = useState<ResourceState<MatchedCoaches>>(UNSET);
  const [coachAvailability, setCoachAvailability] = useState<
    Record<string, ResourceState<CoachAvailability>>
  >({});
  const [dynamicAssessmentEnabled, setDynamicAssessmentEnabled] =
    useState<ResourceState<boolean>>(UNSET);
  const [dynamicAssessmentErrored, setDynamicAssessmentErrored] = useState(false);
  const [dynamicCompletedFlowKeys, setDynamicCompletedFlowKeys] = useState<readonly string[]>([]);

  // Per-resource fetch dedup so a re-render storm (the driver re-runs on every
  // resource transition) can't fire the same request twice. Reset on sign-out /
  // user-identity change (via resetResources) so a subsequent load re-fetches.
  const partnersFetchStarted = useRef<Promise<void> | null>(null);
  const programFetchStarted = useRef<Promise<void> | null>(null);
  const surveyFetchStarted = useRef<Promise<void> | null>(null);
  const teamsFetchStarted = useRef<Promise<void> | null>(null);
  const prescribedFetchStarted = useRef<Promise<void> | null>(null);
  const coachesFetchStarted = useRef<Promise<void> | null>(null);
  const dynamicAssessmentFetchStarted = useRef<Promise<void> | null>(null);
  // Per-provider dedup (keyed by user uuid) for the coach-availability cache.
  const coachAvailabilityFetchStarted = useRef<Record<string, boolean>>({});

  // Re-arm `teams` (Sparta's autoAssignedCoaches path): drop the dedup ref and
  // reset the resource so the driver re-pulls and sees the auto-assigned coach.
  const rearmTeams = useCallback(() => {
    teamsFetchStarted.current = null;
    setTeams(UNSET);
  }, []);

  // metricPoints → ready:true (survey answered for this session).
  const markSurveyComplete = useCallback(() => {
    setMetricPoints({ status: 'ready', value: true });
  }, []);

  // Latch the abandon-survey-on-error state for this session (see the type doc).
  const markDynamicAssessmentErrored = useCallback(() => {
    setDynamicAssessmentErrored(true);
  }, []);

  // programInstances → ready:true (an instance was just created in-flow).
  const markProgramEnrolled = useCallback(() => {
    setProgramInstances({ status: 'ready', value: true });
  }, []);

  // teams (hasCoach) → ready:true without a refetch (see the type doc). Used at
  // the coach-phase exit after a manual coach assignment.
  const markProviderMatchingComplete = useCallback(() => {
    setTeams({ status: 'ready', value: true });
  }, []);

  // Called by the first screen that needs the partner catalog, and by the
  // driver when the resolver asks for `partners`. Deduped + cached for the
  // app's lifetime.
  // `isTester` is passed in by the caller (the user's `isTester`), mirroring
  // Sparta's `loadPartners(currentUser.isTester)`. It is NOT read from a ref
  // here on purpose: the parent ref-sync effect runs AFTER child effects on a
  // given commit, so a screen calling this the moment the user lands would read
  // a stale (undefined) ref. Callers pass the live value instead.
  const loadPartners = useCallback(
    async (isTester = false) => {
      if (partnersFetchStarted.current) {
        return partnersFetchStarted.current;
      }
      setPartners({ status: 'loading', data: null, error: null });
      partnersFetchStarted.current = fetchPartners(isTester)
        .then((data) => setPartners({ status: 'ready', data, error: null }))
        .catch((err: unknown) => {
          const error = err instanceof Error ? err : new Error(String(err));
          setPartners({ status: 'error', data: null, error });
        })
        .finally(() => {
          partnersFetchStarted.current = null;
        });
      return partnersFetchStarted.current;
    },
    [setPartners]
  );

  const ensureProgramInstances = useCallback(
    (userUri: string) => {
      if (programFetchStarted.current) return;
      setProgramInstances({ status: 'loading', value: undefined });
      programFetchStarted.current = fetchUserProgramInstances(userUri)
        .then((instances) => {
          setProgramInstances({ status: 'ready', value: instances.length > 0 });
          // Group-modality enrollment auto-assigns a coach (Sparta's
          // `autoAssignedCoaches`). Re-arm `teams` so the driver re-pulls it and
          // sees the auto-assigned primary coach → the resolver routes to the
          // terminal, skipping the entire coach-matching section.
          if (instances.some((pi) => Boolean(pi.assignedCoachUuid))) {
            rearmTeams();
          }
        })
        .catch(() => setProgramInstances({ status: 'error', value: undefined }))
        .finally(() => {
          programFetchStarted.current = null;
        });
    },
    [rearmTeams]
  );

  const ensureMetricPoints = useCallback(() => {
    if (surveyFetchStarted.current) return;
    setMetricPoints({ status: 'loading', value: undefined });
    // The survey-complete gate is org-aware: an org that doesn't require phq-gad
    // never posts its metrics, so completion keys off the last required
    // screener's metric instead (see isSurveyComplete).
    const org = userDataRef.current?.payingOrganization;
    const flags = {
      preDiabetesRequired: org?.preDiabetesRequired,
      phqGadRequired: org?.phqGadRequired,
    };
    surveyFetchStarted.current = fetchHistoricalMetricPoints()
      .then((metricValues) => isSurveyComplete(metricValues, flags))
      .then((complete) => setMetricPoints({ status: 'ready', value: complete }))
      .catch(() => setMetricPoints({ status: 'error', value: undefined }))
      .finally(() => {
        surveyFetchStarted.current = null;
      });
  }, [userDataRef]);

  const ensureTeams = useCallback(
    (soloTeamCode: string | undefined) => {
      if (teamsFetchStarted.current) return;
      setTeams({ status: 'loading', value: undefined });
      teamsFetchStarted.current = fetchTeamsV5()
        .then((list) => {
          // Capture the solo team's uuid here (the list is already in hand) so
          // sendIntroMessage has the `care-team` channel id without re-fetching.
          soloTeamUuidRef.current = soloTeamCode
            ? list.find((team) => team.code === soloTeamCode)?.uuid
            : undefined;
          // Determines whether to resume pre- or post- provider matching. NOTE:
          // the solo-team-only gate is likely a bug — see the TODO on `hasPrimaryCoach`.
          setTeams({ status: 'ready', value: hasPrimaryCoach(list, soloTeamCode) });
        })
        .catch(() => setTeams({ status: 'error', value: undefined }))
        .finally(() => {
          teamsFetchStarted.current = null;
        });
    },
    [soloTeamUuidRef]
  );

  const ensurePrescribedPrograms = useCallback((userUrn: string) => {
    if (prescribedFetchStarted.current) return;
    setPrescribedPrograms({ status: 'loading', value: undefined });
    prescribedFetchStarted.current = fetchPrescribedPrograms(userUrn)
      .then((catalog) => {
        setPrescribedPrograms({ status: 'ready', value: catalog });
        // Record the recommendation type (Sparta's loadProgramSuccessEpic):
        // Datadog global context + PostHog person attribute. Skipped for an
        // empty catalog (that routes to empty-program).
        if (catalog.programs.length > 0) {
          const { datadog, posthog } = programRecommendationType(catalog);
          void setDatadogGlobalContext({ programRecommendationType: datadog });
          void setUserProperties({ programRecommendType: posthog });
        }
      })
      .catch(() => setPrescribedPrograms({ status: 'error', value: undefined }))
      .finally(() => {
        prescribedFetchStarted.current = null;
      });
  }, []);

  // Non-blocking like ensurePrescribedPrograms: the resolver routes to
  // coach-matching immediately and the screen skeletons while this loads.
  const ensureMatchedCoaches = useCallback((customerUri: string) => {
    if (coachesFetchStarted.current) return;
    setMatchedCoaches({ status: 'loading', value: undefined });
    coachesFetchStarted.current = fetchMatchedCoaches(customerUri)
      .then((matched) => setMatchedCoaches({ status: 'ready', value: matched }))
      .catch(() => setMatchedCoaches({ status: 'error', value: undefined }))
      .finally(() => {
        coachesFetchStarted.current = null;
      });
  }, []);

  // Fetch one provider's initial-consult availability into the shared cache,
  // once per provider (dedup ref). A successful fetch stays deduped — the value
  // is cached for the session — but the flag is cleared on failure so the next
  // mount (e.g. landing on the availability screen) can retry a transient error
  // instead of being poisoned to a permanent `error` state. Keyed by the
  // coach's USER uuid (what the calendaring endpoint expects).
  const ensureCoachAvailability = useCallback((providerUuid: string) => {
    if (!providerUuid || coachAvailabilityFetchStarted.current[providerUuid]) return;
    coachAvailabilityFetchStarted.current[providerUuid] = true;
    setCoachAvailability((prev) => ({
      ...prev,
      [providerUuid]: { status: 'loading', value: undefined },
    }));
    fetchCoachAvailability(providerUuid)
      .then((value) =>
        setCoachAvailability((prev) => ({ ...prev, [providerUuid]: { status: 'ready', value } }))
      )
      .catch(() => {
        delete coachAvailabilityFetchStarted.current[providerUuid];
        setCoachAvailability((prev) => ({
          ...prev,
          [providerUuid]: { status: 'error', value: undefined },
        }));
      });
  }, []);

  // Read the `onb_use_dynamic_assessment` experiment arm once per session (deduped).
  // Multivariate: `test` → value:true. Fails open to `error` (the resolver + survey
  // screens treat anything but `ready:true` as control, so a failed read keeps the
  // legacy flow). Kicked by the driver when the resolver needs it to decide the
  // survey start, and by DynamicAssessmentScreen / AssignedScreenersScreen on
  // mount (belt-and-braces dedupe).
  const ensureDynamicAssessment = useCallback(() => {
    if (dynamicAssessmentFetchStarted.current) return;
    setDynamicAssessmentEnabled({ status: 'loading', value: undefined });
    dynamicAssessmentFetchStarted.current = getFeatureFlag(FEATURE_FLAGS.ONB_USE_DYNAMIC_ASSESSMENT)
      .then((variant) =>
        setDynamicAssessmentEnabled({ status: 'ready', value: variant === 'test' })
      )
      .catch((err) => {
        // Clear the dedup ref on failure so a transient flag-read error can retry
        // on the next mount instead of being poisoned to a permanent error state.
        // A successful read stays deduped — the arm is read once per session.
        dynamicAssessmentFetchStarted.current = null;
        trackEvent('dynamic-assessment-flag-read-failed', {
          error: err instanceof Error ? err.message : String(err),
        });
        setDynamicAssessmentEnabled({ status: 'error', value: undefined });
      });
  }, []);

  const resetResources = useCallback(() => {
    setProgramInstances(UNSET);
    setMetricPoints(UNSET);
    setTeams(UNSET);
    setPrescribedPrograms(UNSET);
    setMatchedCoaches(UNSET);
    setCoachAvailability({});
    setDynamicAssessmentEnabled(UNSET);
    setDynamicAssessmentErrored(false);
    setDynamicCompletedFlowKeys([]);
    programFetchStarted.current = null;
    surveyFetchStarted.current = null;
    teamsFetchStarted.current = null;
    prescribedFetchStarted.current = null;
    coachesFetchStarted.current = null;
    dynamicAssessmentFetchStarted.current = null;
    coachAvailabilityFetchStarted.current = {};
    soloTeamUuidRef.current = undefined;
  }, [soloTeamUuidRef]);

  return {
    programInstances,
    metricPoints,
    teams,
    prescribedPrograms,
    matchedCoaches,
    coachAvailability,
    dynamicAssessmentEnabled,
    dynamicAssessmentErrored,
    markDynamicAssessmentErrored,
    dynamicCompletedFlowKeys,
    setDynamicCompletedFlowKeys,
    loadPartners,
    ensureProgramInstances,
    ensureMetricPoints,
    ensureTeams,
    ensurePrescribedPrograms,
    ensureMatchedCoaches,
    ensureCoachAvailability,
    ensureDynamicAssessment,
    rearmTeams,
    markSurveyComplete,
    markProgramEnrolled,
    markProviderMatchingComplete,
    resetResources,
  };
}
